/**
 * src/components/ui/Pista.jsx
 * La explicación de una cifra, que sale cuando se pide y no antes.
 *
 * Por qué existe (Manuel, 02-09, sobre la ficha de `solofresco`):
 * «Estoy AGOTADO de ver cómo crece la información del panel admin con tanto
 * texto que, poco a poco, se hace inentendible y confuso.»
 *
 * Medido en la zona que señaló: **144 palabras / 984 caracteres de texto gris**,
 * once frases seguidas, todas del mismo tamaño, del mismo color y del mismo
 * peso visual. Ninguna sobraba. Cada una había nacido de una regla buena de la
 * casa —«nunca un cero sin medir» (§3), «sin fallos mudos» (§5)—. El defecto no
 * era escribirlas: era **dejarlas todas encendidas a la vez**, de modo que la
 * salvaguarda de la cifra pesaba lo mismo que la cifra.
 *
 * La regla que implanta este componente:
 *
 *     Una cifra se ve; su explicación se pide.
 *
 * ⚠️⚠️ EL CERROJO, y va en serio: esto es para SALVAGUARDAS y DEFINICIONES —
 * cómo hay que leer un número, de qué ventana sale, por qué un cubo no es lo
 * que parece—. **Nunca para un dato que cambie la decisión.** Si el dato
 * importa, se ve, aunque ocupe. Sin este cerrojo, dentro de tres semanas
 * tendríamos el problema contrario y peor: la información no solo no se vería,
 * es que además no se podría buscar con Ctrl+F ni leer de un vistazo.
 * `tests/test_pantalla_visual_ficha.py` guarda que las cifras de la ficha siguen
 * visibles con todas las pistas cerradas.
 *
 * Por qué no vale el `title` del navegador, que es lo que había hasta hoy:
 * tarda un segundo largo, no se puede diseñar, no se puede seleccionar ni
 * copiar su texto, no admite nada dentro, no existe en pantallas táctiles y no
 * hay forma de comprobarlo en una prueba. Cinco motivos, cada uno suficiente.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'

// Separación entre el disparador y el globo, y margen mínimo con el borde de la
// ventana para que el globo nunca quede medio fuera.
const _SEPARACION = 8
const _MARGEN = 12
const _ANCHO_POR_DEFECTO = 320

/**
 * Coloca el globo respecto del disparador, dentro de la ventana.
 *
 * Va fuera del componente y sin tocar el DOM a propósito: es la única parte con
 * aritmética de verdad, y así se puede probar sin navegador.
 *
 * @param {{top,bottom,left,width}} disparador  rect del disparador
 * @param {{alto,ancho}} globo                  medidas del globo
 * @param {{alto,ancho}} ventana                medidas de la ventana
 * @returns {{top:number,left:number,arriba:boolean}}
 */
export function situarGlobo(disparador, globo, ventana) {
  // Debajo salvo que no quepa; entonces encima. Si no cabe en ningún lado, se
  // queda debajo y se deja que el globo haga su propio scroll: preferimos un
  // globo recortado por abajo a uno que tape la cifra que explica.
  const cabeDebajo = disparador.bottom + _SEPARACION + globo.alto <= ventana.alto - _MARGEN
  const cabeEncima = disparador.top - _SEPARACION - globo.alto >= _MARGEN
  const arriba = !cabeDebajo && cabeEncima

  const top = arriba
    ? disparador.top - _SEPARACION - globo.alto
    : disparador.bottom + _SEPARACION

  // Centrado sobre el disparador, pero pegado al borde antes que salirse.
  const centrado = disparador.left + disparador.width / 2 - globo.ancho / 2
  const maximo = ventana.ancho - _MARGEN - globo.ancho
  const left = Math.max(_MARGEN, Math.min(centrado, Math.max(_MARGEN, maximo)))

  return { top, left, arriba }
}

/**
 * @param {React.ReactNode} contenido  la salvaguarda. Puede llevar cosas dentro
 *                                     (una lista, un botón de copiar).
 * @param {React.ReactNode} [children] lo que se marca. Si no hay, se pinta ⓘ.
 * @param {number} [ancho]             ancho del globo en px.
 * @param {string} [etiqueta]          qué explica, para quien navega a ciegas.
 */
export function Pista({ contenido, children, ancho = _ANCHO_POR_DEFECTO, etiqueta }) {
  const [abierta, setAbierta] = useState(false)
  // «Clavada» = abierta con un clic. Sobrevive a que el ratón se vaya, que es
  // lo que hace falta para leerla con calma, seleccionar su texto o pulsar algo
  // de dentro. Y es el ÚNICO modo que existe en una pantalla táctil, donde no
  // hay «pasar el ratón por encima».
  const [clavada, setClavada] = useState(false)
  const [sitio, setSitio] = useState(null)
  const refDisparador = useRef(null)
  const refGlobo = useRef(null)
  const id = useId()

  const cerrar = useCallback(() => { setAbierta(false); setClavada(false) }, [])

  // Medir DESPUÉS de pintar el globo y ANTES de que el navegador lo enseñe:
  // con useEffect se vería un fotograma en la esquina y luego un salto.
  useLayoutEffect(() => {
    if (!abierta) { setSitio(null); return }
    const d = refDisparador.current?.getBoundingClientRect()
    const g = refGlobo.current?.getBoundingClientRect()
    if (!d || !g) return
    setSitio(situarGlobo(
      { top: d.top, bottom: d.bottom, left: d.left, width: d.width },
      { alto: g.height, ancho: g.width },
      { alto: window.innerHeight, ancho: window.innerWidth },
    ))
  }, [abierta, contenido])

  // Escape cierra siempre. Un clic fuera cierra solo la clavada: la de ratón ya
  // se cierra sola al salir.
  useEffect(() => {
    if (!abierta) return
    const teclado = e => { if (e.key === 'Escape') cerrar() }
    const fuera = e => {
      if (refDisparador.current?.contains(e.target)) return
      if (refGlobo.current?.contains(e.target)) return
      cerrar()
    }
    document.addEventListener('keydown', teclado)
    document.addEventListener('mousedown', fuera)
    return () => {
      document.removeEventListener('keydown', teclado)
      document.removeEventListener('mousedown', fuera)
    }
  }, [abierta, cerrar])

  const marcado = children != null

  return (
    <>
      <button
        type="button"
        ref={refDisparador}
        aria-describedby={abierta ? id : undefined}
        aria-expanded={abierta}
        aria-label={marcado ? undefined : (etiqueta ? `Cómo se lee: ${etiqueta}` : 'Cómo se lee esta cifra')}
        data-pista="disparador"
        onMouseEnter={() => setAbierta(true)}
        onMouseLeave={() => { if (!clavada) setAbierta(false) }}
        onFocus={() => setAbierta(true)}
        onBlur={() => { if (!clavada) setAbierta(false) }}
        onClick={() => {
          if (clavada) { cerrar() } else { setAbierta(true); setClavada(true) }
        }}
        style={{
          font: 'inherit', color: 'inherit', background: 'none', padding: 0,
          border: 'none', cursor: 'help', textAlign: 'inherit',
          // El subrayado punteado es la marca: dice «aquí hay algo más» sin
          // gritar. En el modo ⓘ no hace falta, el glifo ya es la marca.
          borderBottom: marcado ? '1px dotted var(--color-border-2)' : 'none',
        }}
      >
        {marcado ? children : (
          <span aria-hidden="true" style={{
            display: 'inline-block', width: '13px', height: '13px',
            lineHeight: '12px', textAlign: 'center', verticalAlign: 'baseline',
            borderRadius: 'var(--radius-pill)', fontSize: 'var(--text-micro)',
            border: '1px solid var(--color-border-2)', color: 'var(--color-text-3)',
          }}>i</span>
        )}
      </button>

      {abierta && (
        <div
          id={id}
          ref={refGlobo}
          role="tooltip"
          data-pista="globo"
          onMouseEnter={() => setAbierta(true)}
          onMouseLeave={() => { if (!clavada) setAbierta(false) }}
          style={{
            // `fixed` a propósito: la ficha vive dentro de contenedores con
            // overflow oculto, y en `absolute` el globo saldría recortado.
            position: 'fixed', zIndex: 60, width: `${ancho}px`, maxWidth: 'calc(100vw - 24px)',
            maxHeight: '60vh', overflowY: 'auto',
            top: sitio ? `${sitio.top}px` : '-9999px',
            left: sitio ? `${sitio.left}px` : '-9999px',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
            padding: 'var(--space-3)',
            fontSize: 'var(--text-xs)', lineHeight: 1.6,
            color: 'var(--color-text-2)',
            textAlign: 'left', fontWeight: 'normal',
            // Sin `cursor: help` dentro: aquí se lee y se selecciona.
            cursor: 'auto', whiteSpace: 'normal',
          }}
        >
          {contenido}
        </div>
      )}
    </>
  )
}
